/* ============================================================================
 * FILE: ProjectFilterDropdown.tsx
 * PURPOSE: Multi-select dropdown for project filtering with categorization
 * 
 * This file has been refactored from 501 lines to use modular imports.
 * All functions are now organized into logical modules for better maintainability.
 * ============================================================================ */

import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { 
  ChevronDown, 
  Star, 
  Clock, 
  Folder,
  FolderOpen
} from 'lucide-react';
import { useTheme, cn } from '@/ui/theme/ThemeContext';
import { useAuth } from '@/features/auth/AuthContext';
import type { ProjectInfo } from '@/features/projects/services/projectService';
import { getProjectSecondaryLabel } from '@/features/projects/utils/projectCompany';

// Import from extracted modules
import {
  ProjectFilterDropdownProps,
  CategorizedProjects
} from '@/features/tasks/components/ProjectFilter';

import {
  DEFAULT_EXPANDED_SECTIONS,
  DEFAULT_MAX_HEIGHT
} from '@/features/tasks/components/ProjectFilter';

import {
  categorizeProjects,
  getProjectColor
} from '@/features/tasks/components/ProjectFilter';

import {
  toggleSection
} from '@/features/tasks/components/ProjectFilter';

import {
  ProjectSection,
  SearchHeader,
  SelectionFooter,
  EmptyState
} from '@/features/tasks/components/ProjectFilter';
import {
  closeProjectFilterDropdown,
  getProjectFilterDropdownStoreState,
  openProjectFilterDropdown,
  subscribeProjectFilterDropdownStore,
  updateProjectFilterDropdown,
} from './projectFilterDropdownStore';
import {
  CLOSE_ALL_DROPDOWNS_ID,
  DROPDOWN_MOTION_DURATION_MS,
  announceDropdownClosed,
  announceDropdownOpen,
  subscribeDropdownCoordinator,
  useDropdownTransition,
} from '@/ui/components/overlays/dropdownCoordinator';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { getFilterControlWidthPx } from '@/ui/components/filterControlSizing';
import { APP_Z_INDEX } from '@/ui/theme/layers';

const useProjectFilterDropdownStore = () =>
  useSyncExternalStore(
    subscribeProjectFilterDropdownStore,
    getProjectFilterDropdownStoreState,
    getProjectFilterDropdownStoreState
  );

const PROJECT_FILTER_DROPDOWN_ID = 'project-filter-dropdown';

interface ProjectFilterTriggerController {
  isControlled: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const projectFilterTriggerControllers = new WeakMap<HTMLElement, ProjectFilterTriggerController>();

const requestProjectFilterDropdownClose = (triggerElement: HTMLElement | null) => {
  const controller = triggerElement
    ? projectFilterTriggerControllers.get(triggerElement)
    : undefined;
  controller?.onOpenChange?.(false);
  if (!controller?.isControlled) {
    closeProjectFilterDropdown();
  }
};

interface ProjectFilterDropdownPanelProps {
  selectedProjects: string[];
  onProjectsChange: (projectIds: string[]) => void;
  projects: ProjectInfo[];
  onCreateProject?: () => void;
  placeholder: string;
  showColorIndicators: boolean;
  maxHeight: number;
}

const ProjectFilterDropdownPanel: React.FC<ProjectFilterDropdownPanelProps> = ({
  selectedProjects,
  onProjectsChange,
  projects,
  onCreateProject,
  placeholder: _placeholder,
  showColorIndicators,
  maxHeight
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const { hasPermission } = useAuth();
  const canCreateProject = hasPermission('projects', 'create');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(DEFAULT_EXPANDED_SECTIONS)
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Override default expanded sections if needed
  useEffect(() => {
    setExpandedSections(new Set(DEFAULT_EXPANDED_SECTIONS));
  }, [setExpandedSections]);

  useEffect(() => {
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, []);

  // Ensure projects is an array, default to empty array if not
  const safeProjects = useMemo(() => Array.isArray(projects) ? projects : [], [projects]);

  // Categorize projects (with safety check for projects array)
  const categorizedProjects: CategorizedProjects = useMemo(() => {
    return categorizeProjects(safeProjects, searchQuery);
  }, [safeProjects, searchQuery]);

  // Event handler wrappers
  const handleToggleSection = (section: string) => {
    toggleSection(section, expandedSections, setExpandedSections);
  };

  const handleToggleProject = (projectId: string) => {
    const nextSelectedProjects = selectedProjects.includes(projectId)
      ? selectedProjects.filter(id => id !== projectId)
      : [...selectedProjects, projectId];
    onProjectsChange(nextSelectedProjects);
  };

  const handleSelectAllClick = () => {
    const nextSelectedProjects = selectedProjects.length === safeProjects.length
      ? []
      : safeProjects.map(project => project.projectId);
    onProjectsChange(nextSelectedProjects);
  };

  const handleCreateProject = () => {
    announceDropdownClosed(PROJECT_FILTER_DROPDOWN_ID);
    requestProjectFilterDropdownClose(getProjectFilterDropdownStoreState().triggerElement);
    onCreateProject?.();
  };

  const handleClearSelection = () => {
    onProjectsChange([]);
  };

  return (
    <>
      <SearchHeader
        searchQuery={searchQuery}
        theme={theme}
        searchInputRef={searchInputRef}
        onSearchChange={setSearchQuery}
        onSelectAll={handleSelectAllClick}
        onCreateProject={handleCreateProject}
        selectAllText={selectedProjects.length === safeProjects.length ? t('Deselect All') : t('Select All')}
        showCreateButton={!!onCreateProject && canCreateProject}
      />

      <div className="overflow-y-auto" style={{ maxHeight }}>
        <ProjectSection
          sectionId="pinned"
          title={t('Pinned')}
          projects={categorizedProjects.pinned}
          icon={<Star size={14} className="text-yellow-500" />}
          isExpanded={expandedSections.has('pinned')}
          selectedProjects={selectedProjects}
          showColorIndicators={showColorIndicators}
          theme={theme}
          onToggleSection={() => handleToggleSection('pinned')}
          onToggleProject={handleToggleProject}
        />
        <ProjectSection
          sectionId="favorites"
          title={t('Favorites')}
          projects={categorizedProjects.favorites}
          icon={<Star size={14} />}
          isExpanded={expandedSections.has('favorites')}
          selectedProjects={selectedProjects}
          showColorIndicators={showColorIndicators}
          theme={theme}
          onToggleSection={() => handleToggleSection('favorites')}
          onToggleProject={handleToggleProject}
        />
        <ProjectSection
          sectionId="recent"
          title={t('Recent')}
          projects={categorizedProjects.recent}
          icon={<Clock size={14} />}
          isExpanded={expandedSections.has('recent')}
          selectedProjects={selectedProjects}
          showColorIndicators={showColorIndicators}
          theme={theme}
          onToggleSection={() => handleToggleSection('recent')}
          onToggleProject={handleToggleProject}
        />
        <ProjectSection
          sectionId="all"
          title={t('All Projects')}
          projects={categorizedProjects.all}
          icon={<Folder size={14} />}
          isExpanded={expandedSections.has('all')}
          selectedProjects={selectedProjects}
          showColorIndicators={showColorIndicators}
          theme={theme}
          onToggleSection={() => handleToggleSection('all')}
          onToggleProject={handleToggleProject}
        />

        <EmptyState
          searchQuery={searchQuery}
          hasProjects={safeProjects.length > 0}
          theme={theme}
          onCreateProject={onCreateProject}
          showCreateButton={canCreateProject}
        />
      </div>

      <SelectionFooter
        selectedCount={selectedProjects.length}
        theme={theme}
        onClearSelection={handleClearSelection}
      />
    </>
  );
};

const ProjectFilterDropdownBase: React.FC<ProjectFilterDropdownProps> = ({
  selectedProjects,
  onProjectsChange,
  projects,
  onCreateProject,
  className,
  placeholder = 'Select projects...',
  showColorIndicators = true,
  maxHeight = DEFAULT_MAX_HEIGHT,
  isLoading = false,
  isOpen: controlledIsOpen,
  onOpenChange,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownState = useProjectFilterDropdownStore();
  const isControlled = typeof controlledIsOpen === 'boolean';
  const ownsOpenDropdown = dropdownState.isOpen && dropdownState.triggerElement === buttonRef.current;
  const isOpen = ownsOpenDropdown && (!isControlled || controlledIsOpen);
  const controlledOpenAppliedRef = useRef(false);

  const safeProjects = useMemo(() => Array.isArray(projects) ? projects : [], [projects]);
  const localizedPlaceholder = placeholder === 'Select projects...' ? t('Select projects...') : placeholder;
  const displayText = useMemo(() => {
    if (selectedProjects.length === 0) {
      return localizedPlaceholder;
    }

    if (selectedProjects.length === 1) {
      const project = safeProjects.find((entry) => entry.projectId === selectedProjects[0]);
      return project?.projectName || t('Unknown Project');
    }

    return t('{{count}} projects selected', { count: selectedProjects.length });
  }, [localizedPlaceholder, safeProjects, selectedProjects, t]);
  const triggerWidthPx = useMemo(
    () =>
      getFilterControlWidthPx(
        [localizedPlaceholder, displayText],
        { minWidthPx: 132, maxWidthPx: 260, paddingPx: 92 }
      ),
    [displayText, localizedPlaceholder]
  );

  const syncDropdownState = React.useCallback(() => {
    const currentState = getProjectFilterDropdownStoreState();
    if (
      !buttonRef.current ||
      !currentState.isOpen ||
      currentState.triggerElement !== buttonRef.current
    ) {
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    updateProjectFilterDropdown({
      anchor: {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
      triggerElement: buttonRef.current,
      selectedProjects,
      projects: safeProjects,
      onProjectsChange,
      onCreateProject,
      placeholder,
      showColorIndicators,
      maxHeight,
    });
  }, [selectedProjects, safeProjects, onProjectsChange, onCreateProject, placeholder, showColorIndicators, maxHeight]);

  useEffect(() => {
    syncDropdownState();
  }, [syncDropdownState]);

  useEffect(() => {
    const triggerElement = buttonRef.current;
    if (!triggerElement) {
      return;
    }

    const controller = { isControlled, onOpenChange };
    projectFilterTriggerControllers.set(triggerElement, controller);
    return () => {
      if (projectFilterTriggerControllers.get(triggerElement) === controller) {
        projectFilterTriggerControllers.delete(triggerElement);
      }
    };
  }, [isControlled, onOpenChange]);

  useEffect(() => {
    const triggerElement = buttonRef.current;
    return () => {
      controlledOpenAppliedRef.current = false;
      if (getProjectFilterDropdownStoreState().triggerElement === triggerElement) {
        closeProjectFilterDropdown();
      }
    };
  }, []);

  useEffect(() => {
    if (!isControlled || !buttonRef.current) {
      return;
    }

    if (!controlledIsOpen) {
      controlledOpenAppliedRef.current = false;
      if (ownsOpenDropdown) {
        announceDropdownClosed(PROJECT_FILTER_DROPDOWN_ID);
        closeProjectFilterDropdown();
      }
      return;
    }

    if (controlledOpenAppliedRef.current) return;
    controlledOpenAppliedRef.current = true;
    if (ownsOpenDropdown) return;

    const currentState = getProjectFilterDropdownStoreState();
    if (currentState.isOpen && currentState.triggerElement !== buttonRef.current) {
      requestProjectFilterDropdownClose(currentState.triggerElement);
    }

    const rect = buttonRef.current.getBoundingClientRect();
    announceDropdownOpen(PROJECT_FILTER_DROPDOWN_ID);
    openProjectFilterDropdown({
      anchor: {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
      triggerElement: buttonRef.current,
      selectedProjects,
      projects: safeProjects,
      onProjectsChange,
      onCreateProject,
      placeholder,
      showColorIndicators,
      maxHeight,
    });
  }, [
    controlledIsOpen,
    isControlled,
    maxHeight,
    onCreateProject,
    onProjectsChange,
    ownsOpenDropdown,
    placeholder,
    safeProjects,
    selectedProjects,
    showColorIndicators,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updateAnchor = () => {
      syncDropdownState();
    };

    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);

    return () => {
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
    };
  }, [isOpen, syncDropdownState]);

  const handleTriggerClick = () => {
    if (isOpen) {
      announceDropdownClosed(PROJECT_FILTER_DROPDOWN_ID);
      if (ownsOpenDropdown) {
        requestProjectFilterDropdownClose(buttonRef.current);
      } else {
        onOpenChange?.(false);
      }
      return;
    }

    if (isControlled) {
      onOpenChange?.(true);
      return;
    }

    if (!buttonRef.current) {
      return;
    }

    const currentState = getProjectFilterDropdownStoreState();
    if (currentState.isOpen && currentState.triggerElement !== buttonRef.current) {
      requestProjectFilterDropdownClose(currentState.triggerElement);
    }
    announceDropdownOpen(PROJECT_FILTER_DROPDOWN_ID);
    onOpenChange?.(true);
    const rect = buttonRef.current.getBoundingClientRect();
    openProjectFilterDropdown({
      anchor: {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
      triggerElement: buttonRef.current,
      selectedProjects,
      projects: safeProjects,
      onProjectsChange,
      onCreateProject,
      placeholder,
      showColorIndicators,
      maxHeight,
    });
  };

  return (
    <div className={cn("relative shrink-0", className)}>
      <button
        type="button"
        ref={buttonRef}
        onClick={handleTriggerClick}
        aria-label={localizedPlaceholder}
        title={`${localizedPlaceholder}: ${displayText}`}
        aria-expanded={isOpen}
        data-testid="project-filter-trigger"
        className={cn(
          "flex h-7 shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-md border bg-transparent px-2 text-[12px] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/70",
          theme.borderPrimary,
          theme.textPrimary,
          theme.surfaceHover,
          isOpen && cn(theme.hoverBg, theme.textPrimary)
        )}
        style={{ width: `${triggerWidthPx}px` }}
      >
        <div className="pm-project-filter-main flex min-w-0 flex-1 items-center gap-2">
          {selectedProjects.length > 0 ? <FolderOpen size={14} /> : <Folder size={14} />}
          <span className="pm-project-filter-label truncate whitespace-nowrap">{displayText}</span>
        </div>
        <div className="pm-project-filter-details flex shrink-0 items-center gap-1 pr-0.5">
          {selectedProjects.length > 0 && (
            <div className="flex -space-x-1">
              {selectedProjects.slice(0, 3).map(id => {
                const project = safeProjects.find(p => p.projectId === id);
                if (!project) return null;
                return (
                  <div
                    key={id}
                    className="w-3.5 h-3.5 rounded-full border-2 border-white dark:border-gray-800"
                    style={{ backgroundColor: getProjectColor(id, project.color) }}
                    title={project.projectName}
                  />
                );
              })}
              {selectedProjects.length > 3 && (
                <div className={cn(
                  "w-3.5 h-3.5 rounded-full border-2 border-white dark:border-gray-800",
                  "bg-gray-400 dark:bg-gray-600 flex items-center justify-center"
                )}>
                  <span className="text-[8px] text-white font-bold">
                    +{selectedProjects.length - 3}
                  </span>
                </div>
              )}
            </div>
          )}
          <div
            className="flex h-4 w-4 items-center justify-center"
            aria-live="polite"
            aria-label={isLoading ? t('Refreshing projects') : undefined}
          >
            <div
              className={cn(
                "h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent",
                isLoading ? "animate-spin opacity-100" : "opacity-0"
              )}
            />
          </div>
          <ChevronDown size={14} className={cn("transition-transform", isOpen && "rotate-180")} />
        </div>
      </button>
    </div>
  );
};

export const ProjectFilterDropdown = React.memo(ProjectFilterDropdownBase);

export const ProjectFilterDropdownHost: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const dropdownState = useProjectFilterDropdownStore();
  const { shouldRender, isActive, exitImmediately } = useDropdownTransition(
    dropdownState.isOpen,
    DROPDOWN_MOTION_DURATION_MS,
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const panelWidthPx = useMemo(() => {
    const projectLabels = dropdownState.projects.flatMap((project) => {
      const secondaryLabel = getProjectSecondaryLabel(project);
      return secondaryLabel ? [project.projectName, secondaryLabel] : [project.projectName];
    });

    return Math.max(
      dropdownState.anchor?.width ?? 0,
      getFilterControlWidthPx(projectLabels, { minWidthPx: 320, maxWidthPx: 560, paddingPx: 112 })
    );
  }, [dropdownState.anchor?.width, dropdownState.projects]);

  useEffect(() => {
    const unsubscribe = subscribeDropdownCoordinator((openDropdownId) => {
      if (
        openDropdownId === CLOSE_ALL_DROPDOWNS_ID ||
        (openDropdownId && openDropdownId !== PROJECT_FILTER_DROPDOWN_ID)
      ) {
        exitImmediately();
        requestProjectFilterDropdownClose(
          getProjectFilterDropdownStoreState().triggerElement,
        );
      }
    });
    return () => { unsubscribe(); };
  }, [exitImmediately]);

  useEffect(() => {
    if (!shouldRender || !isActive || !dropdownState.anchor) {
      return;
    }

    const anchor = dropdownState.anchor;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && panelRef.current?.contains(target)) {
        return;
      }

      const { left, top, width, height } = anchor;
      if (
        event.clientX >= left &&
        event.clientX <= left + width &&
        event.clientY >= top &&
        event.clientY <= top + height
      ) {
        return;
      }

      announceDropdownClosed(PROJECT_FILTER_DROPDOWN_ID);
      requestProjectFilterDropdownClose(dropdownState.triggerElement);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [shouldRender, isActive, dropdownState.anchor, dropdownState.triggerElement]);

  useEffect(() => {
    if (!shouldRender || !isActive) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const focusTarget = dropdownState.triggerElement;
      announceDropdownClosed(PROJECT_FILTER_DROPDOWN_ID);
      requestProjectFilterDropdownClose(focusTarget);
      window.requestAnimationFrame(() => {
        focusTarget?.focus();
      });
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [dropdownState.triggerElement, shouldRender, isActive]);

  if (!shouldRender || !dropdownState.anchor || !dropdownState.onProjectsChange) {
    return null;
  }

  return (
    <>
      <div
        ref={panelRef}
        data-testid="project-filter-dropdown"
        role="dialog"
        aria-label={t(dropdownState.placeholder)}
        data-motion-state={isActive ? 'open' : 'closed'}
        className={cn(
          "fixed rounded-lg border shadow-xl [--dropdown-slide-radius:0.5rem] transition-[clip-path,transform] will-change-[clip-path,transform] motion-reduce:animate-none motion-reduce:transform-none motion-reduce:transition-none",
          theme.bgPrimary,
          theme.borderPrimary,
          isActive
            ? "dropdown-slide-enter translate-y-0 opacity-100 [clip-path:inset(0_0_0_0_round_0.5rem)] duration-[320ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            : "dropdown-slide-exit -translate-y-3 opacity-100 [clip-path:inset(0_0_100%_0_round_0.5rem)] pointer-events-none duration-[320ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        )}
        style={{
          top: `${dropdownState.anchor.bottom + 4}px`,
          left: `${dropdownState.anchor.left}px`,
          width: `${panelWidthPx}px`,
          zIndex: APP_Z_INDEX.dropdown,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <ProjectFilterDropdownPanel
          selectedProjects={dropdownState.selectedProjects}
          onProjectsChange={dropdownState.onProjectsChange}
          projects={dropdownState.projects}
          onCreateProject={dropdownState.onCreateProject}
          placeholder={dropdownState.placeholder}
          showColorIndicators={dropdownState.showColorIndicators}
          maxHeight={dropdownState.maxHeight}
        />
      </div>
    </>
  );
};
